import { useCallback, useEffect, useState } from 'react';
import { procurementApi, type PurchaseRequestResponse, type RfqSummary } from '@/api/procurement.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { PurchaseRequestSelect, SupplierMultiSelect } from '@/components/selects';
import {
  createProductLineDraft,
  parseProductLines,
  ProductLineItemsEditor,
  type ProductLineDraft,
} from '@/components/forms/ProductLineItemsEditor';
import { Button } from '@/components/ui/Button';
import { DateTimePicker, defaultDateTimeLocalValue } from '@/components/ui/DateTimePicker';
import { Textarea } from '@/components/ui/Textarea';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { usePurchaseRequestOptions } from '@/hooks/usePurchaseRequestOptions';
import { formatDate } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

function defaultRfqDeadline(): string {
  return defaultDateTimeLocalValue(24 * 7);
}

function linesFromPurchaseRequest(pr: PurchaseRequestResponse | null): ProductLineDraft[] {
  if (!pr?.items?.length) {
    return [createProductLineDraft({ quantity: '1' })];
  }
  return pr.items.map((item) =>
    createProductLineDraft({
      productId: String(item.productId),
      quantity: String(item.quantity),
    }),
  );
}

export function RfqsPage() {
  const pagination = usePagination();
  const { getLabel: getPurchaseRequestLabel } = usePurchaseRequestOptions();
  const [items, setItems] = useState<RfqSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchaseRequestId, setPurchaseRequestId] = useState('');
  const [supplierIds, setSupplierIds] = useState<string[]>([]);
  const [lines, setLines] = useState<ProductLineDraft[]>([createProductLineDraft({ quantity: '10' })]);
  const [deadlineAt, setDeadlineAt] = useState(defaultRfqDeadline);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    procurementApi
      .listRfqs({ page: pagination.page, size: pagination.size })
      .then((p) => {
        setItems(p.content);
        pagination.applyPageResponse(p);
      })
      .catch((e) => showApiError(e, 'Failed to load RFQs'))
      .finally(() => setLoading(false));
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setPurchaseRequestId('');
    setSupplierIds([]);
    setLines([createProductLineDraft({ quantity: '10' })]);
    setDeadlineAt(defaultRfqDeadline());
    setNotes('');
  };

  const handlePurchaseRequestChange = (pr: PurchaseRequestResponse | null) => {
    setLines(linesFromPurchaseRequest(pr));
  };

  const create = async () => {
    if (supplierIds.length === 0) {
      notify.error('Select at least one supplier');
      return;
    }
    const itemsPayload = parseProductLines(lines, 'basic');
    if (itemsPayload.length === 0) {
      notify.error('Add at least one product line with quantity');
      return;
    }
    if (!deadlineAt) {
      notify.error('Set a response deadline');
      return;
    }
    setSaving(true);
    try {
      await procurementApi.createRfq({
        purchaseRequestId: purchaseRequestId ? Number(purchaseRequestId) : undefined,
        deadlineAt,
        notes: notes.trim() || undefined,
        supplierIds: supplierIds.map(Number),
        items: itemsPayload,
      });
      notify.success('RFQ created and sent to suppliers');
      resetForm();
      load();
    } catch (err) {
      showApiError(err, 'Create RFQ failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="RFQs"
        description="Request for quotation — invite multiple suppliers with multiple product lines"
        breadcrumbs={[{ label: 'Procurement', to: '/procurement' }, { label: 'RFQs' }]}
      />

      <section className="card space-y-4 p-4">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Create RFQ</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <PurchaseRequestSelect
            label="Purchase request (optional)"
            value={purchaseRequestId}
            onChange={setPurchaseRequestId}
            onRequestChange={handlePurchaseRequestChange}
            approvedOnly
            placeholder="Select approved purchase request"
          />
          <DateTimePicker
            label="Response deadline *"
            value={deadlineAt}
            onChange={setDeadlineAt}
            required
          />
          <div className="md:col-span-2">
            <SupplierMultiSelect
              label="Suppliers *"
              values={supplierIds}
              onChange={setSupplierIds}
              required
            />
          </div>
          <div className="md:col-span-2">
            <Textarea
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional instructions for suppliers"
              rows={2}
            />
          </div>
        </div>
        <ProductLineItemsEditor lines={lines} onChange={setLines} />
        <Button loading={saving} onClick={() => void create()}>
          Create RFQ
        </Button>
      </section>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left dark:border-slate-700">
              <th className="p-3">Number</th>
              <th className="p-3">Status</th>
              <th className="p-3">Purchase request</th>
              <th className="p-3">Deadline</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3 font-medium">{r.rfqNumber}</td>
                <td className="p-3">{r.rfqStatus}</td>
                <td className="p-3">
                  {r.purchaseRequestId ? getPurchaseRequestLabel(r.purchaseRequestId) : '—'}
                </td>
                <td className="p-3">{r.deadlineAt ? formatDate(r.deadlineAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={items.length > 0}
        />
      </div>
    </div>
  );
}
